import { useCallback, useEffect, useRef, useState } from "react";

interface UseClickOutSideWithStateProps {
  onSubmit: () => void;
  isDirty: boolean;
}

export const useClickOutSideWithState = ({ onSubmit, isDirty }: UseClickOutSideWithStateProps) => {
  const [dirty, setDirty] = useState<boolean>(isDirty);
  const nodeRef = useRef<any>(null);

  const handleSubmit = useCallback(() => {
    onSubmit();
  }, [onSubmit, dirty]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (nodeRef.current && !nodeRef.current.contains(event.target as Node)) {
        setDirty(false);
        handleSubmit();
      }
    };

    if (dirty) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dirty, handleSubmit]);

  return { dirty, setDirty, onSubmit, nodeRef };
};

interface useClickOutsideWithStateMultipleProps {
  onSubmit: () => boolean | undefined;
  isDirty: boolean;
  refs: React.RefObject<HTMLElement>[];
}

export const useClickOutsideWithStateMultiple = ({
  onSubmit,
  isDirty,
  refs,
}: useClickOutsideWithStateMultipleProps) => {
  const [dirty, setDirty] = useState<boolean>(isDirty);

  const handleSubmit = useCallback(() => {
    if (dirty) {
      const shouldClose = onSubmit();
      if (shouldClose !== false) {
        setDirty(false);
      }
    }
  }, [dirty, onSubmit]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (refs.every((ref) => ref.current && !ref.current.contains(event.target as Node))) {
        handleSubmit();
      }
    };

    if (dirty) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dirty, handleSubmit, refs]);

  return { dirty, setDirty };
};
